import { useCallback, useEffect, useRef, useState } from "react";

import { useSearchCustomerFolderByStringLazyQuery } from "src/core/gql/graphql";

const MIN_QUERY_LEN = 3;
const DEBOUNCE_MS = 250;

export const useSearchFolders = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isDebouncing, setIsDebouncing] = useState(false);

  const [searchClients, { data, loading }] = useSearchCustomerFolderByStringLazyQuery();

  const debounceRef = useRef<number | null>(null);

  const clearDebounce = useCallback(() => {
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchQuery(value);

      clearDebounce();

      const trimmed = value.trim();
      if (trimmed.length < MIN_QUERY_LEN) {
        setIsDebouncing(false);
        setDebouncedQuery("");
        return;
      }

      setIsDebouncing(true);
      debounceRef.current = window.setTimeout(() => {
        setDebouncedQuery(value);
        setIsDebouncing(false);
      }, DEBOUNCE_MS);
    },
    [clearDebounce]
  );

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (trimmed.length >= MIN_QUERY_LEN) {
      searchClients({ variables: { searchString: debouncedQuery, limit: 10 } });
    }
  }, [debouncedQuery, searchClients]);

  useEffect(() => clearDebounce, [clearDebounce]);

  const handleSelect = useCallback((folderId: string, onSelect?: (folderId: string) => void) => {
    onSelect?.(folderId);
    setSearchQuery("");
    setDebouncedQuery("");
    setIsDebouncing(false);
  }, []);

  const handleClose = useCallback(() => {
    setSearchQuery("");
    setDebouncedQuery("");
    setIsDebouncing(false);
  }, []);

  const hasResults = (data?.customerFoldersPage.summaries?.length ?? 0) > 0;

  const shouldShowDropdown = searchQuery.trim().length >= MIN_QUERY_LEN;

  const isLoading = loading || isDebouncing;

  return {
    searchQuery,
    data,
    loading: isLoading,
    hasResults,
    shouldShowDropdown,
    handleSearch,
    handleSelect,
    handleClose,
  };
};
